/**
 * Torn's public reference data (items, bank rates, city shops, honors, medals, Hall of Fame, factions, ranked wars,
 * rackets, territory wars, organized-crime scenarios) as the website's "Torn" pages show it.
 *
 * The server reads it with its OWN key (apps/api/src/torn-data.ts: cached on disk, a few calls a minute at most) and
 * serves it in these shapes; the parsers below turn Torn's answers (API v2, or v1 where v2 has no such selection) into
 * them. Browser-safe (no node: imports).
 *
 * Shapes: v2 items/honors/medals/hof/factionhof/organizedcrimes/rankedwarreport and v1 cityshops/rackets/territorywars/rankedwars are
 * written from Torn's docs as we know them; where a field name is not certain, the parser accepts the likely spellings
 * and skips what it can't read (it never throws) — `null` means "not this shape" (the server then tries the next way
 * of asking, or says Torn's answer wasn't understood).
 */

import { readWarReport, type WarReport } from './war';

export type TornRefKind = 'items' | 'bank' | 'cityshops' | 'honors' | 'medals' | 'hof' | 'factionhof' | 'rankedwars' | 'rankedwarreport' | 'rackets' | 'territorywars' | 'organizedcrimes' | 'playerhof';

export const TORN_REF_KINDS: readonly TornRefKind[] = ['items', 'bank', 'cityshops', 'honors', 'medals', 'hof', 'factionhof', 'rankedwars', 'rankedwarreport', 'rackets', 'territorywars', 'organizedcrimes', 'playerhof'];

/** Hall of Fame boards (Torn API v2 /torn/hof?cat=…) — the label we show. Battle stats are left out on purpose. */
export const HOF_CATEGORIES: ReadonlyArray<{ id: string; label: string; money?: boolean; time?: boolean }> = [
  { id: 'level', label: 'Level' },
  { id: 'networth', label: 'Networth', money: true },
  { id: 'rank', label: 'Rank' },
  { id: 'awards', label: 'Awards' },
  { id: 'offenses', label: 'Criminal offenses' },
  { id: 'attacks', label: 'Attacks won' },
  { id: 'defends', label: 'Defends won' },
  { id: 'busts', label: 'Busts' },
  { id: 'revives', label: 'Revives' },
  { id: 'traveltime', label: 'Time traveled', time: true },
  { id: 'workstats', label: 'Working stats' },
  { id: 'racingskill', label: 'Racing skill' },
  { id: 'racingpoints', label: 'Racing points' },
  { id: 'racingwins', label: 'Racing wins' },
];

/** Faction Hall of Fame boards (v2 /torn/factionhof?cat=…). */
export const FACTION_HOF_CATEGORIES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'respect', label: 'Respect' },
  { id: 'chain', label: 'Best chain' },
  { id: 'rank', label: 'Rank' },
];

/** Hall of Fame pages we ask Torn for: 100 a page, the top 1,000 at most. */
export const HOF_PAGE = 100;
export const HOF_MAX_OFFSET = 900;

/** The argument a kind takes (null: none), or null when it isn't a good one. */
export function refArg(kind: TornRefKind, raw: string | null | undefined): string | null | false {
  const a = (raw ?? '').trim().toLowerCase();
  if (kind === 'hof') {
    const [cat, off = '0'] = a.split(':');
    const offset = Number(off);
    if (!HOF_CATEGORIES.some((c) => c.id === cat) || !Number.isInteger(offset) || offset < 0 || offset > HOF_MAX_OFFSET || offset % HOF_PAGE) return false;
    return `${cat}:${offset}`;
  }
  if (kind === 'factionhof') return FACTION_HOF_CATEGORIES.some((c) => c.id === a) ? a : false;
  if (kind === 'playerhof' || kind === 'rankedwarreport') return /^[1-9]\d{0,8}$/.test(a) ? a : false;
  return a ? false : null;
}

// ---------------------------------------------------------------- shapes

export type { WarReport, WarReportSide, WarReportMember } from './war';

export interface RefItem {
  id: number;
  name: string;
  type: string;
  subType: string | null;
  description: string;
  effect: string | null;
  requirement: string | null;
  /** What a city shop sells it for (null: not sold in the city). */
  buyPrice: number | null;
  /** What a city shop pays for it. */
  sellPrice: number | null;
  /** Torn's market value (average market price). */
  marketValue: number | null;
  circulation: number | null;
  tradable: boolean | null;
  /** Who sells it ("Big Al's Gun Shop", "Mexico"…). */
  vendor: string | null;
}

export interface BankRate {
  /** Investment length in days (7, 14, 30, 60, 90). */
  days: number;
  label: string;
  /** Yearly rate, percent, as Torn shows it. */
  rate: number;
}

export interface CityShop {
  id: string;
  name: string;
  items: Array<{ id: number; name: string; type: string; price: number; stock: number | null }>;
}

export interface RefAward {
  id: number;
  name: string;
  description: string;
  type: string;
  rarity: string | null;
  circulation: number | null;
}

export interface HofRow {
  position: number;
  id: number;
  name: string;
  level: number | null;
  factionId: number | null;
  value: number;
}

export interface FactionHofRow {
  position: number;
  id: number;
  name: string;
  members: number | null;
  rank: string | null;
  value: number;
}

export interface RefRankedWar {
  id: number;
  start: number;
  end: number | null;
  target: number | null;
  winner: number | null;
  factions: Array<{ id: number; name: string; score: number; chain: number | null }>;
}

export interface Racket {
  territory: string;
  name: string;
  level: number | null;
  reward: string;
  factionId: number | null;
  created: number | null;
  changed: number | null;
}

export interface TerritoryWar {
  territory: string;
  id: number | null;
  assaulting: number | null;
  defending: number | null;
  score: number | null;
  required: number | null;
  started: number | null;
  ends: number | null;
}

export interface OcScenario {
  name: string;
  description: string;
  difficulty: number | null;
  /** Who can start it (Torn's spawn: "Level 2"…), if Torn says. */
  spawn: string | null;
  slots: Array<{ name: string; item: string | null; itemUsed: boolean | null }>;
}

/** One player's Hall of Fame places: category → value and rank. */
export type PlayerHof = Record<string, { value: number; rank: number }>;

export interface TornRefData {
  items: RefItem[];
  bank: BankRate[];
  cityshops: CityShop[];
  honors: RefAward[];
  medals: RefAward[];
  hof: HofRow[];
  factionhof: FactionHofRow[];
  rankedwars: RefRankedWar[];
  /** One ranked war's report (arg: the war's id): both sides, each member's attacks and score, ranks and rewards. */
  rankedwarreport: WarReport;
  rackets: Racket[];
  territorywars: TerritoryWar[];
  organizedcrimes: OcScenario[];
  playerhof: PlayerHof;
}

/** What GET /api/torn/<kind> answers. */
export interface TornRefAnswer<K extends TornRefKind = TornRefKind> {
  kind: K;
  arg: string | null;
  /** When Torn was asked (ms). */
  at: number;
  /** When the server asks again (ms). */
  next: number;
  /** Torn couldn't be asked just now: this is the last copy. */
  stale: boolean;
  data: TornRefData[K];
  /** organizedcrimes: how much each seat matters (scenario key → seat key → weight, adds up to 100). */
  weights?: Record<string, Record<string, number>>;
}

// ---------------------------------------------------------------- helpers

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => {
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  if (typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x.replace(/,/g, '')))) return Number(x.replace(/,/g, ''));
  return null;
};
const str = (x: unknown): string | null => (typeof x === 'string' ? x : typeof x === 'number' ? String(x) : null);
/** Torn's lists come as arrays (v2) or objects keyed by id (v1): [id, value] pairs either way. */
function entries(x: unknown): Array<[string | null, unknown]> {
  if (Array.isArray(x)) return x.map((v) => [null, v]);
  const o = obj(x);
  return o ? Object.entries(o) : [];
}
const posInt = (x: unknown): number | null => {
  const n = num(x);
  return n !== null && Number.isInteger(n) && n > 0 ? n : null;
};
/** "Hustler" twice → "Hustler #1", "Hustler #2" (the way Torn's crimes page names seats). */
function numberSeats<T extends { name: string }>(slots: T[]): T[] {
  const count = new Map<string, number>();
  for (const s of slots) count.set(s.name, (count.get(s.name) ?? 0) + 1);
  const seen = new Map<string, number>();
  return slots.map((s) => {
    if ((count.get(s.name) ?? 0) < 2 || /#\d+$/.test(s.name)) return s;
    const n = (seen.get(s.name) ?? 0) + 1;
    seen.set(s.name, n);
    return { ...s, name: `${s.name} #${n}` };
  });
}

// ---------------------------------------------------------------- parsers

/** v2 /torn/items (`items: [...]`, value.market_price) or v1 torn?selections=items (`items: { id: {...} }`, market_value). */
export function parseItems(body: unknown): RefItem[] | null {
  const b = obj(body);
  if (!b || b.items === undefined) return null;
  const out: RefItem[] = [];
  for (const [k, raw] of entries(b.items)) {
    const it = obj(raw);
    const id = posInt(it?.id) ?? posInt(k);
    const name = str(it?.name);
    if (!it || !id || !name) continue;
    const value = obj(it.value);
    const vendor = obj(value?.vendor);
    out.push({
      id,
      name,
      type: str(it.type) ?? 'Other',
      subType: str(it.sub_type) ?? str(it.weapon_type),
      description: str(it.description) ?? '',
      effect: str(it.effect) || null,
      requirement: str(it.requirement) || null,
      buyPrice: num(value?.buy_price ?? it.buy_price) || null,
      sellPrice: num(value?.sell_price ?? it.sell_price) || null,
      marketValue: num(value?.market_price ?? it.market_value) || null,
      circulation: num(it.circulation),
      tradable: typeof it.is_tradable === 'boolean' ? it.is_tradable : null,
      vendor: vendor ? [str(vendor.name), str(vendor.country)].filter((x) => x && x !== 'Torn').join(', ') || null : null,
    });
  }
  return out.sort((a, b) => a.id - b.id);
}

const BANK_DAYS: Record<string, number> = { '1w': 7, '2w': 14, '1m': 30, '2m': 60, '3m': 90 };
const BANK_LABEL: Record<number, string> = { 7: '1 week', 14: '2 weeks', 30: '1 month', 60: '2 months', 90: '3 months' };

/** v2 /torn/bank (`bank: [{ duration|days, rate }]`, shape partly guessed) or v1 torn?selections=bank (`bank: { "1w": "0.86", … }`). */
export function parseBank(body: unknown): BankRate[] | null {
  const b = obj(body);
  if (!b || b.bank === undefined) return null;
  const out: BankRate[] = [];
  for (const [k, raw] of entries(b.bank)) {
    const o = obj(raw);
    const rate = o ? num(o.rate ?? o.apr ?? o.interest) : num(raw);
    const days = o ? (num(o.days) ?? BANK_DAYS[str(o.duration) ?? ''] ?? num(o.duration)) : BANK_DAYS[k ?? ''];
    if (rate === null || !days) continue;
    out.push({ days, label: BANK_LABEL[days] ?? `${days} days`, rate });
  }
  return out.length ? out.sort((a, b) => a.days - b.days) : null;
}

/** v1 torn?selections=cityshops: `cityshops: { "<shop id>": { name, inventory: { "<item id>": { name, type, price, in_stock } } } }`. */
export function parseCityShops(body: unknown): CityShop[] | null {
  const b = obj(body);
  if (!b || b.cityshops === undefined) return null;
  const out: CityShop[] = [];
  for (const [k, raw] of entries(b.cityshops)) {
    const s = obj(raw);
    const name = str(s?.name);
    if (!s || !name) continue;
    const items: CityShop['items'] = [];
    for (const [ik, iraw] of entries(s.inventory ?? s.items)) {
      const it = obj(iraw);
      const id = posInt(it?.id) ?? posInt(ik);
      const price = num(it?.price);
      if (!it || !id || price === null) continue;
      items.push({ id, name: str(it.name) ?? `Item ${id}`, type: str(it.type) ?? '', price, stock: num(it.in_stock ?? it.stock) });
    }
    out.push({ id: str(s.id) ?? k ?? name, name, items });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** v2 /torn/honors or /torn/medals (`honors: [...]`, type { id, name } or a string) or v1 (`honors: { id: {...} }`). */
export function parseAwards(body: unknown, field: 'honors' | 'medals'): RefAward[] | null {
  const b = obj(body);
  if (!b || b[field] === undefined) return null;
  const out: RefAward[] = [];
  for (const [k, raw] of entries(b[field])) {
    const a = obj(raw);
    const id = posInt(a?.id) ?? posInt(k);
    const name = str(a?.name);
    if (!a || !id || !name) continue;
    const t = obj(a.type);
    out.push({ id, name, description: str(a.description) ?? '', type: str(t?.name) ?? str(a.type) ?? str(a.category) ?? 'Other', rarity: str(a.rarity), circulation: num(a.circulation) });
  }
  return out.sort((a, b) => a.id - b.id);
}

/** v2 /torn/hof?cat=…: `hof: [{ id, username, faction_id, level, position|rank, value }]`. */
export function parseHof(body: unknown): HofRow[] | null {
  const b = obj(body);
  if (!b || b.hof === undefined) return null;
  const out: HofRow[] = [];
  for (const [k, raw] of entries(b.hof)) {
    const r = obj(raw);
    const id = posInt(r?.id) ?? posInt(r?.user_id) ?? posInt(k);
    const value = num(r?.value);
    if (!r || !id || value === null) continue;
    out.push({ position: posInt(r.position) ?? posInt(r.rank) ?? out.length + 1, id, name: str(r.username) ?? str(r.name) ?? `Player ${id}`, level: num(r.level), factionId: posInt(r.faction_id) ?? posInt(obj(r.faction)?.id), value });
  }
  return out.sort((a, b) => a.position - b.position);
}

/** v2 /torn/factionhof?cat=…: `factionhof: [{ id, name, members, position, rank, value }]`. */
export function parseFactionHof(body: unknown): FactionHofRow[] | null {
  const b = obj(body);
  const list = b?.factionhof ?? b?.factionHof ?? b?.hof;
  if (!b || list === undefined) return null;
  const out: FactionHofRow[] = [];
  for (const [k, raw] of entries(list)) {
    const r = obj(raw);
    const id = posInt(r?.id) ?? posInt(r?.faction_id) ?? posInt(k);
    const value = num(r?.value);
    if (!r || !id || value === null) continue;
    const rank = obj(r.rank);
    out.push({ position: posInt(r.position) ?? out.length + 1, id, name: str(r.name) ?? `Faction ${id}`, members: num(r.members), rank: rank ? [str(rank.name), str(rank.division)].filter(Boolean).join(' ') || null : typeof r.rank === 'string' ? r.rank : null, value });
  }
  return out.sort((a, b) => a.position - b.position);
}

/**
 * Ranked wars: v1 torn?selections=rankedwars (`rankedwars: { "<war id>": { factions: { "<id>": { name, score, chain } }, war: { start, end, target, winner } } }`)
 * or v2 /faction/warfare?cat=ranked (`warfare: [{ id, start, end, target, winner, factions: [...] }]`, also accepted as
 * `warfare: { ranked: [...] }` or `rankedwars: [...]`).
 */
export function parseRankedWars(body: unknown): RefRankedWar[] | null {
  const b = obj(body);
  const list = b?.rankedwars ?? (Array.isArray(b?.warfare) ? b.warfare : (obj(b?.warfare)?.ranked ?? obj(b?.warfare)?.rankedwars));
  if (!b || list === undefined || list === null) return null;
  const out: RefRankedWar[] = [];
  for (const [k, raw] of entries(list)) {
    const w = obj(raw);
    const war = obj(w?.war) ?? w;
    const id = posInt(w?.id) ?? posInt(w?.war_id) ?? posInt(k);
    const start = num(war?.start);
    if (!w || !war || !id || !start) continue;
    const factions = entries(w.factions)
      .map(([fk, f]) => {
        const o = obj(f);
        const fid = posInt(o?.id) ?? posInt(fk);
        return o && fid ? { id: fid, name: str(o.name) ?? `Faction ${fid}`, score: num(o.score) ?? 0, chain: num(o.chain) } : null;
      })
      .filter((f): f is RefRankedWar['factions'][number] => !!f);
    out.push({ id, start, end: num(war.end) || null, target: num(war.target), winner: posInt(war.winner), factions });
  }
  return out.sort((a, b) => b.start - a.start);
}

/** v1 torn?selections=rackets: `rackets: { "<territory>": { name, level, reward, created, changed, faction } }` (v2 list accepted too). */
export function parseRackets(body: unknown): Racket[] | null {
  const b = obj(body);
  if (!b || b.rackets === undefined) return null;
  const out: Racket[] = [];
  for (const [k, raw] of entries(b.rackets)) {
    const r = obj(raw);
    const territory = str(r?.territory) ?? str(r?.territory_id) ?? k;
    const name = str(r?.name);
    if (!r || !territory || !name) continue;
    out.push({ territory, name, level: num(r.level), reward: str(r.reward) ?? str(r.description) ?? '', factionId: posInt(r.faction) ?? posInt(r.faction_id) ?? posInt(obj(r.faction)?.id), created: num(r.created ?? r.created_at), changed: num(r.changed ?? r.changed_at) });
  }
  return out.sort((a, b) => (b.level ?? 0) - (a.level ?? 0) || a.name.localeCompare(b.name));
}

/** v1 torn?selections=territorywars: `territorywars: { "<territory>": { territory_war_id, assaulting_faction, defending_faction, score, required_score, started, ends } }`. */
export function parseTerritoryWars(body: unknown): TerritoryWar[] | null {
  const b = obj(body);
  if (!b || b.territorywars === undefined) return null;
  const out: TerritoryWar[] = [];
  for (const [k, raw] of entries(b.territorywars)) {
    const w = obj(raw);
    const territory = str(w?.territory) ?? str(w?.territory_id) ?? k;
    if (!w || !territory) continue;
    out.push({
      territory,
      id: posInt(w.territory_war_id) ?? posInt(w.id),
      assaulting: posInt(w.assaulting_faction) ?? posInt(obj(w.assaulting_faction)?.id) ?? posInt(w.attacker),
      defending: posInt(w.defending_faction) ?? posInt(obj(w.defending_faction)?.id) ?? posInt(w.defender),
      score: num(w.score),
      required: num(w.required_score ?? w.target),
      started: num(w.started ?? w.start),
      ends: num(w.ends ?? w.end),
    });
  }
  return out.sort((a, b) => (a.ends ?? Infinity) - (b.ends ?? Infinity));
}

/** v2 /torn/organizedcrimes: `organizedcrimes: [{ name, description, difficulty, spawn: { level, name }, slots: [{ name, required_item: { name, is_used } }] }]`. */
export function parseOcScenarios(body: unknown): OcScenario[] | null {
  const b = obj(body);
  const list = b?.organizedcrimes ?? b?.organisedcrimes;
  if (!b || list === undefined) return null;
  const out: OcScenario[] = [];
  for (const [, raw] of entries(list)) {
    const c = obj(raw);
    const name = str(c?.name);
    if (!c || !name) continue;
    const spawn = obj(c.spawn);
    const slots = entries(c.slots)
      .map(([, s]) => {
        const o = obj(s);
        const item = obj(o?.required_item ?? o?.item);
        const n = str(o?.name) ?? str(o?.position);
        return o && n ? { name: n, item: item ? (str(item.name) ?? (posInt(item.id) ? `Item ${item.id}` : null)) : null, itemUsed: item && typeof item.is_used === 'boolean' ? item.is_used : null } : null;
      })
      .filter((s): s is OcScenario['slots'][number] => !!s);
    out.push({ name, description: str(c.description) ?? '', difficulty: num(c.difficulty ?? c.level), spawn: spawn ? (str(spawn.name) ?? (num(spawn.level) !== null ? `Level ${spawn.level}` : null)) : null, slots: numberSeats(slots) });
  }
  return out.sort((a, b) => (a.difficulty ?? 99) - (b.difficulty ?? 99) || a.name.localeCompare(b.name));
}

/** v2 /faction/{warId}/rankedwarreport or v1 torn/{warId}?selections=rankedwarreport (war.ts reads both). */
export function parseRankedWarReport(body: unknown, warId: number): WarReport | null {
  return readWarReport(body, warId);
}

/** v2 /user/{id}/hof: `hof: { attacks: { value, rank }, … }` (arrays of { type|category, value, rank } accepted too). */
export function parsePlayerHof(body: unknown): PlayerHof | null {
  const b = obj(body);
  if (!b || b.hof === undefined) return null;
  const out: PlayerHof = {};
  for (const [k, raw] of entries(b.hof)) {
    const r = obj(raw);
    const cat = k ?? str(r?.type) ?? str(r?.category);
    const value = num(r?.value);
    const rank = posInt(r?.rank) ?? posInt(r?.position);
    if (!cat || value === null || !rank) continue;
    out[cat] = { value, rank };
  }
  return out;
}
